// The one Margin % implementation. IB-986 surfaces it on four screens built in
// three different stacks — the department / cost-centre and Expenditure summary
// DataTables, the tiles above them, and the React Budget overview — and each of
// those had grown its own copy, with the dash rule and the decimal convention
// already drifting apart (a department with cost and no mark-up read "–" on one
// screen and "0%" on another). There is no JS test runner in this repo to catch
// that, so the figure lives here and nowhere else.
//
// mix.copy'd classic scripts cannot import an ES module, so app.js also exposes
// this on window as InbudMargin for expenditureTable.js — the same arrangement
// InbudCurrency uses.

import { decimalUpTo, parseLocaleNumber } from 'Helpers/currencyFormat';
import { formatFigure } from 'Helpers/formatService';

/** Which figure Margin % divides mark-up by, per the organisation's setting. */
export const MARGIN_BASIS_CLIENT_TOTAL = 'client_total';
export const MARGIN_BASIS_COST = 'cost';

/**
 * Shown wherever a Margin % has no divisor behind it. A 0% would read as a
 * measured margin on a row that has no cost to measure one against.
 */
export const MARGIN_NO_DIVISOR = '–';

/**
 * A figure that may arrive as a payload number or as already-rendered cell text.
 *
 * Cell text carries the CURRENCY'S separators, so it goes through the engine's
 * parser rather than a blanket comma strip — "14.250.000,50" would otherwise read
 * as 14.25. A raw number is used as-is: stringifying it first would have the same
 * parser read "14250000.5" as 142500005 under a dot-grouping currency.
 */
function readFigure(value) {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }

    // A percentage cell's trailing '%' is not a separator the parser tolerates —
    // it rejects the whole string, so "100%" would read as 0.
    const parsed = parseLocaleNumber(
        String(value ?? '')
            .replace(/%\s*$/, '')
            .trim(),
    );

    return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Margin % as a number, or null when there is no divisor to take one of.
 *
 * Derived from the two figures beside it every time it is drawn — never read from
 * a stored percentage, which is an arithmetic sum belonging to no single amount.
 * Never capped: a mark-up above 100% is a deliberate client requirement, so 150
 * on a cost of 100 gives 150.
 */
export function marginPercentageValue(markUp, cost, basis) {
    const costAmount = readFigure(cost);
    const markUpAmount = readFigure(markUp);
    const divisor = basis === MARGIN_BASIS_COST ? costAmount : costAmount + markUpAmount;

    if (divisor === 0) return null;

    return (markUpAmount / divisor) * 100;
}

/**
 * Margin % formatted for a cell, or an en dash where there is no divisor. A
 * margin of exactly 0% shows the zero dash too, like every other zero figure;
 * stat tiles pass `{ zeroDash: false }` and keep "0%".
 *
 * `decimalUpTo(_, 2)` matches the mark-up % cells this sits beside — "122.22%",
 * "100%" — rather than padding to a fixed 2dp, and unlike
 * formatToFixedMoneyWithLocale it leaves a negative signed rather than bracketing
 * the number and stranding the % outside the brackets.
 */
export function marginPercentage(markUp, cost, basis, { zeroDash = true } = {}) {
    const percentage = marginPercentageValue(markUp, cost, basis);

    if (percentage === null) return MARGIN_NO_DIVISOR;

    if (!zeroDash) return decimalUpTo(percentage, 2) + '%';

    return formatFigure(percentage, decimalUpTo, { precision: 2, suffix: '%' });
}
